<template>
  <div class="page-container">
    <PageHeader title="上传任务" show-breadcrumb />

    <SearchPanel>
      <el-input v-model.trim="query.zfName" placeholder="任务名称" clearable size="small" @keyup.enter.native="handleSearch" />
      <template #actions>
        <el-button type="primary" icon="el-icon-search" size="small" @click="handleSearch">查询</el-button>
        <el-button icon="el-icon-refresh" size="small" @click="handleReset">重置</el-button>
        <el-button type="success" icon="el-icon-plus" size="small" @click="handleAdd">新增任务</el-button>
      </template>
    </SearchPanel>

    <TablePanel title="上传任务列表">
      <el-table :data="list" v-loading="loading" size="small" stripe :height="tableHeight">
        <el-table-column type="index" label="序号" width="80" align="center" />
        <el-table-column prop="zfName" label="任务名称" min-width="200" align="center" show-overflow-tooltip />
        <el-table-column prop="createtime" label="创建时间" min-width="160" align="center" />
        <el-table-column label="操作" width="260" align="center">
          <template slot-scope="{ row }">
            <el-button type="text" size="mini" style="color:#409eff" @click="handleViewRecords(row)">查看记录</el-button>
            <el-button type="text" size="mini" style="color:#e6a23c" @click="handleEdit(row)">编辑</el-button>
            <el-button type="text" size="mini" style="color:#f56c6c" @click="handleDelete(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        :current-page="page.currentPage"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="page.pageSize"
        :total="page.total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handlePageChange"
      />
    </TablePanel>

    <!-- 新增/编辑任务弹框 -->
    <el-dialog :title="dialogType === 'add' ? '新增上传任务' : '编辑上传任务'" :visible.sync="dialogVisible" width="480px" v-dialog-drag-resize>
      <el-form ref="formRef" :model="form" :rules="rules" label-width="90px" size="small">
        <el-form-item label="任务名称" prop="zfName">
          <el-input v-model.trim="form.zfName" placeholder="请输入任务名称" maxlength="50" show-word-limit />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" :loading="submitLoading" @click="submitDialog">确定</el-button>
      </div>
    </el-dialog>

    <!-- 上传记录列表弹框：查看某任务下所有用户的上传记录 -->
    <el-dialog :title="`上传记录 - ${currentTask ? currentTask.zfName : ''}`" :visible.sync="recordVisible" width="900px" append-to-body v-dialog-drag-resize>
      <!-- 查询条件：系统管理员可按项目筛选学员提交记录 -->
      <div v-if="isAdmin" class="record-filter">
        <span class="record-filter__label">所属项目：</span>
        <el-select v-model="recordQuery.projectId" placeholder="全部项目" clearable size="small" filterable style="width:280px" @change="fetchRecordList">
          <el-option v-for="p in projectOptions" :key="p.zapId" :label="p.zapName" :value="p.zapId" />
        </el-select>
        <el-button type="primary" icon="el-icon-search" size="small" style="margin-left:12px" @click="fetchRecordList">查询</el-button>
        <el-button icon="el-icon-refresh" size="small" @click="resetRecordQuery">重置</el-button>
      </div>
      <el-table :data="recordList" v-loading="recordLoading" size="small" stripe max-height="440">
        <el-table-column type="index" label="序号" width="60" align="center" />
        <el-table-column prop="createusername" label="学员" min-width="120" align="center" show-overflow-tooltip />
        <el-table-column v-if="isAdmin" label="所属项目" min-width="160" align="center" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ projectName(row.avOwnerId) }}</template>
        </el-table-column>
        <el-table-column label="文件概览" min-width="320" align="center">
          <template slot-scope="{ row }">
            <span class="record-summary">{{ formatSummary(row.zfuFile) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="createtime" label="提交时间" min-width="150" align="center" />
        <el-table-column label="操作" width="100" align="center">
          <template slot-scope="{ row }">
            <el-button type="text" size="mini" style="color:#409eff" @click="handleViewFiles(row)">查看文件</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div slot="footer">
        <el-button type="primary" size="small" @click="recordVisible = false">关闭</el-button>
      </div>
    </el-dialog>

    <!-- 文件详情弹框：按类型分组展示某条记录的所有文件 -->
    <el-dialog title="文件详情" :visible.sync="fileVisible" width="640px" append-to-body v-dialog-drag-resize>
      <div v-loading="fileLoading">
        <template v-if="fileGroups.length">
          <div v-for="g in fileGroups" :key="g.fileType" class="file-group">
            <div class="file-group__title">{{ fileTypeText(g.fileType) }}（{{ g.fileType === 5 ? (g.text ? g.text.length + ' 字' : '0 字') : g.files.length }}）</div>
            <!-- 文本域 -->
            <div v-if="g.fileType === 5" class="file-group__text">{{ g.text }}</div>
            <!-- 图片 -->
            <div v-else-if="g.fileType === 1" class="file-group__images">
              <el-image v-for="(f, i) in g.files" :key="i" :src="f.url" fit="cover"
                :preview-src-list="g.files.map(x => x.url)" :initial-index="i" class="file-image-item" />
            </div>
            <!-- 视频 -->
            <div v-else-if="g.fileType === 2" class="file-group__videos">
              <video v-for="(f, i) in g.files" :key="i" :src="f.url" controls class="file-video-item" />
            </div>
            <!-- 文件（显示文件名） -->
            <div v-else-if="g.fileType === 3" class="file-group__files">
              <div v-for="(f, i) in g.files" :key="i" class="file-row">
                <i class="el-icon-document"></i>
                <span class="file-row__name">{{ f.name }}</span>
                <el-button type="primary" size="mini" @click="openUrl(f.url)">下载/打开</el-button>
              </div>
            </div>
            <!-- 链接 -->
            <div v-else-if="g.fileType === 4" class="file-group__links">
              <div v-for="(f, i) in g.files" :key="i" class="file-row">
                <i class="el-icon-link"></i>
                <span class="file-row__name">{{ f.url }}</span>
                <el-button type="primary" size="mini" @click="openUrl(f.url)">打开</el-button>
              </div>
            </div>
          </div>
        </template>
        <el-empty v-else description="暂无文件" />
      </div>
      <div slot="footer">
        <el-button type="primary" size="small" @click="fileVisible = false">关闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import PageHeader from '@/components/PageHeader.vue'
import SearchPanel from '@/components/SearchPanel.vue'
import TablePanel from '@/components/TablePanel.vue'
import { getZqsxFileList, addZqsxFile, updateZqsxFile, deleteZqsxFile, getZqsxFileUserList, getZqsxFileUserById, getZqsxAuthProjectList } from '@/api/api'

// 文件类型字典：1图片 2视频 3文件 4链接（与小程序端 util.js FILE_TYPE 保持一致）
const FILE_TYPE_TEXT = { 1: '图片', 2: '视频', 3: '文件', 4: '链接', 5: '文本' }

export default {
  name: 'FileTask',
  components: { PageHeader, SearchPanel, TablePanel },
  data () {
    return {
      loading: false,
      tableHeight: 400,
      list: [],
      query: { zfName: '' },
      page: { currentPage: 1, pageSize: 10, total: 0 },
      // 任务新增/编辑
      dialogVisible: false,
      dialogType: 'add',
      submitLoading: false,
      form: { zfId: null, zfName: '' },
      rules: {
        zfName: [{ required: true, message: '请输入任务名称', trigger: 'blur' }]
      },
      // 上传记录列表
      recordVisible: false,
      recordLoading: false,
      recordList: [],
      currentTask: null,
      // 项目下拉数据（系统管理员按项目筛选用）
      projectOptions: [],
      // 记录查询条件（系统管理员按项目筛选）
      recordQuery: { projectId: null },
      // 文件详情
      fileVisible: false,
      fileLoading: false,
      fileGroups: []
    }
  },
  computed: {
    // 系统管理员（zauPost=1）可按项目筛选；项目管理员只能看自己项目，无需筛选
    isAdmin () {
      const u = this.$store.state.user.userInfo || {}
      return u.zauPost === 1
    }
  },
  created () { this.fetchList() },
  mounted () {
    this.$nextTick(this.calcTableHeight)
    window.addEventListener('resize', this.calcTableHeight)
  },
  beforeDestroy () { window.removeEventListener('resize', this.calcTableHeight) },
  methods: {
    calcTableHeight () {
      const el = this.$el.querySelector('.el-table')
      if (!el) return
      this.tableHeight = window.innerHeight - el.getBoundingClientRect().top - 80
    },
    async fetchList () {
      this.loading = true
      try {
        const params = { ...this.query, currentPage: this.page.currentPage, pageSize: this.page.pageSize }
        const res = await getZqsxFileList(params)
        this.list = res.data || []
        this.page.total = res.total || 0
      } catch (e) { this.list = []; this.page.total = 0 } finally { this.loading = false }
    },
    handleSearch () { this.page.currentPage = 1; this.fetchList() },
    handleReset () { this.query = { zfName: '' }; this.handleSearch() },
    handleSizeChange (size) { this.page.pageSize = size; this.handleSearch() },
    handlePageChange (page) { this.page.currentPage = page; this.fetchList() },
    handleAdd () {
      this.dialogType = 'add'
      this.form = { zfId: null, zfName: '' }
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.formRef && this.$refs.formRef.clearValidate())
    },
    handleEdit (row) {
      this.dialogType = 'edit'
      this.form = { zfId: row.zfId, zfName: row.zfName || '' }
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.formRef && this.$refs.formRef.clearValidate())
    },
    submitDialog () {
      this.$refs.formRef.validate(async valid => {
        if (!valid) return
        this.submitLoading = true
        try {
          if (this.dialogType === 'add') {
            await addZqsxFile({ zfName: this.form.zfName })
            this.$message.success('新增成功')
          } else {
            await updateZqsxFile({ zfId: this.form.zfId, zfName: this.form.zfName })
            this.$message.success('编辑成功')
          }
          this.dialogVisible = false
          this.fetchList()
        } catch (e) {} finally { this.submitLoading = false }
      })
    },
    async handleDelete (row) {
      try { await this.$confirm(`确定删除任务「${row.zfName}」？`, '提示', { type: 'warning' }) } catch { return }
      try {
        await deleteZqsxFile({ zfId: row.zfId })
        this.$message.success('删除成功')
        this.fetchList()
      } catch (e) {}
    },
    // 查看某任务下的上传记录（系统管理员可按项目筛选）
    async handleViewRecords (row) {
      this.currentTask = row
      this.recordQuery = { projectId: null }
      this.recordList = []
      this.recordVisible = true
      // 系统管理员：预加载项目列表供筛选
      if (this.isAdmin && !this.projectOptions.length) {
        await this.fetchProjects()
      }
      await this.fetchRecordList()
    },
    // 拉取项目下拉数据（系统管理员用）
    async fetchProjects () {
      try {
        const res = await getZqsxAuthProjectList({ currentPage: 1, pageSize: 9999 })
        this.projectOptions = res.data || []
      } catch (e) { this.projectOptions = [] }
    },
    // 按当前查询条件拉取上传记录
    async fetchRecordList () {
      if (!this.currentTask) return
      this.recordLoading = true
      try {
        const params = { zfId: this.currentTask.zfId, currentPage: 1, pageSize: 9999 }
        // 系统管理员按项目筛选；项目管理员不传（后端按所属项目自动过滤）
        if (this.isAdmin && this.recordQuery.projectId) {
          params.projectId = this.recordQuery.projectId
        }
        const res = await getZqsxFileUserList(params)
        this.recordList = res.data || []
      } catch (e) { this.recordList = [] } finally { this.recordLoading = false }
    },
    // 重置查询条件并刷新
    resetRecordQuery () {
      this.recordQuery = { projectId: null }
      this.fetchRecordList()
    },
    // 根据项目ID（avOwnerId）查项目名（用于表格展示）
    projectName (avOwnerId) {
      if (!avOwnerId) return '—'
      const p = this.projectOptions.find(x => x.zapId === avOwnerId)
      return p ? p.zapName : '项目' + avOwnerId
    },
    // 查看某条记录的全部文件（解析 zfuFile JSON 字符串，按类型分组）
    async handleViewFiles (row) {
      this.fileVisible = true
      this.fileLoading = true
      this.fileGroups = []
      try {
        const res = await getZqsxFileUserById({ zfuId: row.zfuId })
        const record = res.data || row
        this.fileGroups = this.parseFileRecord(record.zfuFile)
      } catch (e) { this.fileGroups = [] } finally { this.fileLoading = false }
    },
    // 解析 zfuFile（JSON 字符串）为 [{fileType, files:[{url,name}]}] 数组
    // 兼容：新格式 files / 旧格式 urls（自动补 name）/ 单 URL 字符串
    parseFileRecord (raw) {
      const urlToName = (u) => {
        if (!u) return '文件'
        const clean = String(u).split('?')[0]
        const segs = clean.split('/')
        return decodeURIComponent(segs[segs.length - 1] || '文件')
      }
      const normalize = (groups) => groups
        .filter(g => g && ((g.files && g.files.length) || (g.urls && g.urls.length) || (Number(g.fileType) === 5 && g.text)))
        .map(g => {
          // 文本域类型：保留 text
          if (Number(g.fileType) === 5) return { fileType: 5, text: g.text || '', files: [] }
          const files = (g.files && g.files.length)
            ? g.files.filter(f => f && f.url).map(f => ({ url: f.url, name: f.name || urlToName(f.url) }))
            : (g.urls || []).filter(u => u).map(u => ({ url: u, name: urlToName(u) }))
          return { fileType: Number(g.fileType), files }
        })
      if (!raw) return []
      const str = String(raw).trim()
      if (str.startsWith('[')) {
        try {
          const arr = JSON.parse(str)
          if (Array.isArray(arr)) {
            // 对象数组（新/旧格式）
            if (arr.length && typeof arr[0] === 'object' && arr[0] !== null) return normalize(arr)
            // 纯字符串数组
            return [{ fileType: 3, files: arr.filter(u => u && typeof u === 'string').map(u => ({ url: u, name: urlToName(u) })) }]
          }
        } catch (e) { /* 兜底 */ }
      }
      return [{ fileType: 3, files: [{ url: str, name: urlToName(str) }] }]
    },
    // 记录列表的文件概览文字
    formatSummary (raw) {
      const groups = this.parseFileRecord(raw)
      if (!groups.length) return '—'
      return groups.map(g => `${FILE_TYPE_TEXT[g.fileType] || '文件'}×${g.fileType === 5 ? (g.text ? g.text.length + '字' : '0字') : g.files.length}`).join('，')
    },
    fileTypeText (t) { return FILE_TYPE_TEXT[t] || '文件' },
    // 从 URL 提取文件名
    extractFileName (url) {
      if (!url) return '文件'
      const clean = url.split('?')[0]
      const segs = clean.split('/')
      return decodeURIComponent(segs[segs.length - 1] || '文件')
    },
    openUrl (url) { window.open(url, '_blank') }
  }
}
</script>

<style lang="scss" scoped>
.record-summary {
  font-size: 12px;
  color: #64748b;
}
/* 记录弹框：筛选条件区 */
.record-filter {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 12px 16px;
  margin-bottom: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}
.record-filter__label {
  font-size: 13px;
  color: #475569;
  flex-shrink: 0;
}
/* 文件详情分组 */
.file-group {
  margin-bottom: 20px;
  &:last-child { margin-bottom: 0; }
}
.file-group__title {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 10px;
  padding-left: 8px;
  border-left: 3px solid #1677ff;
}
/* 文本域内容块 */
.file-group__text {
  font-size: 13px;
  color: #475569;
  line-height: 1.7;
  white-space: pre-wrap;
  word-break: break-all;
  padding: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
}
.file-group__images {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.file-image-item {
  width: 100px;
  height: 100px;
  border-radius: 6px;
  cursor: pointer;
}
.file-group__videos {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.file-video-item {
  width: 100%;
  max-height: 320px;
  border-radius: 6px;
}
.file-group__files, .file-group__links {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.file-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  > i:first-child { color: #1677ff; font-size: 16px; }
}
.file-row__name {
  flex: 1;
  font-size: 13px;
  color: #475569;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
</style>
